"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useEnquirySubmit } from "@/hooks/useEnquirySubmit";
import { cn } from "@/lib/utils";
import { Mail } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useId, useState } from "react";

interface EventContactDialogProps {
  eventId: string;
  className?: string;
}

interface EventContactForm {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const EMPTY_FORM: EventContactForm = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export function EventContactDialog({
  eventId,
  className,
}: EventContactDialogProps) {
  const t = useTranslations("eventContact");
  const fieldId = useId();
  const { status, errorMessage, submit, reset } = useEnquirySubmit({
    path: "/api/events/contact",
    mapError: async (response) =>
      response.status === 429 ? t("rateLimited") : t("error"),
    networkError: t("error"),
  });
  const [open, setOpen] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [consent, setConsent] = useState(false);
  const [formData, setFormData] = useState<EventContactForm>(EMPTY_FORM);

  const resetForm = () => {
    reset();
    setConsentError(false);
    setConsent(false);
    setFormData(EMPTY_FORM);
  };

  const handleOpenChange = (next: boolean) => {
    if (next) resetForm();
    setOpen(next);
  };

  const openDialog = () => {
    resetForm();
    setOpen(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }

    setConsentError(false);
    await submit({
      ...formData,
      consent: true,
      eventId,
    });
  };

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        className={cn(
          "w-full rounded-full py-2 h-12 px-4 gap-2 text-sm font-medium",
          className,
        )}
        onClick={openDialog}
      >
        <Mail className="h-5 w-5" />
        {t("button")}
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          {status === "success" ? (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <Image
                src="/images/signup-success-trophy.png"
                alt=""
                width={112}
                height={112}
                className="h-28 w-28 object-contain"
              />
              <DialogHeader className="items-center sm:text-center">
                <DialogTitle>{t("successTitle")}</DialogTitle>
                <DialogDescription className="text-center">
                  {t("successMessage")}
                </DialogDescription>
              </DialogHeader>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t("title")}</DialogTitle>
                <DialogDescription>{t("subtitle")}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor={`${fieldId}-name`}>{t("name")}</Label>
                    <Input
                      id={`${fieldId}-name`}
                      name="name"
                      placeholder={t("namePlaceholder")}
                      value={formData.name}
                      onChange={handleChange}
                      maxLength={100}
                      required
                      disabled={status === "submitting"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`${fieldId}-email`}>{t("email")}</Label>
                    <Input
                      id={`${fieldId}-email`}
                      name="email"
                      type="email"
                      placeholder={t("emailPlaceholder")}
                      value={formData.email}
                      onChange={handleChange}
                      maxLength={255}
                      required
                      disabled={status === "submitting"}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldId}-subject`}>{t("subject")}</Label>
                  <Input
                    id={`${fieldId}-subject`}
                    name="subject"
                    placeholder={t("subjectPlaceholder")}
                    value={formData.subject}
                    onChange={handleChange}
                    maxLength={200}
                    required
                    disabled={status === "submitting"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${fieldId}-message`}>{t("message")}</Label>
                  <Textarea
                    id={`${fieldId}-message`}
                    name="message"
                    placeholder={t("messagePlaceholder")}
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={2000}
                    rows={4}
                    required
                    disabled={status === "submitting"}
                  />
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`${fieldId}-consent`}
                    checked={consent}
                    onCheckedChange={(checked) => {
                      const next = checked === true;
                      setConsent(next);
                      if (next) setConsentError(false);
                    }}
                    disabled={status === "submitting"}
                    aria-invalid={consentError}
                    aria-describedby={
                      consentError ? `${fieldId}-consent-error` : undefined
                    }
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`${fieldId}-consent`}
                    className="font-normal leading-snug"
                  >
                    {t("consent")}
                  </Label>
                </div>
                {consentError && (
                  <p
                    id={`${fieldId}-consent-error`}
                    role="alert"
                    className="text-sm text-destructive"
                  >
                    {t("consentRequired")}
                  </p>
                )}
                {errorMessage && (
                  <p role="alert" className="text-sm text-destructive">
                    {errorMessage}
                  </p>
                )}
                <Button
                  type="submit"
                  aria-busy={status === "submitting"}
                  className="w-full h-12"
                >
                  {status === "submitting" ? t("sending") : t("send")}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
